import { useEffect, useMemo, useState } from "react";

/**
 * Shared atmospherics + frame-loop hook for every Vault Breaker game stage.
 *
 * Hoisted from games/pressure-plate/core.tsx so future games can reuse the
 * same primitives without duplicating the rAF subscription pool or the
 * dust/grain/light-cone components. The pressure-plate version is now a
 * re-export shim.
 */

// ---- Global frame-loop hook ------------------------------------------------
// Single rAF subscription shared across every component that calls useTime,
// so we don't burn 6 separate frame loops on a single page.

const subs = new Set<(t: number) => void>();
let started = false;
let t0: number | null = null;

function startFrameLoop(): void {
  if (started) return;
  started = true;
  const tick = (now: number): void => {
    if (t0 === null) t0 = now;
    const t = (now - t0) / 1000;
    subs.forEach((fn) => fn(t));
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

/** Continuous-time hook in seconds. Backed by a single shared rAF loop. */
export function useTime(): number {
  const [t, setT] = useState(0);
  useEffect(() => {
    startFrameLoop();
    subs.add(setT);
    return () => {
      subs.delete(setT);
    };
  }, []);
  return t;
}

// ---- Light cone, vignette, dust, grain ------------------------------------

export type AtmosphereHue = "warm" | "cyan" | "red";

export function LightCone({
  intensity = 1,
  hue = "warm",
}: {
  intensity?: number;
  hue?: AtmosphereHue;
}) {
  const c =
    hue === "red"
      ? "rgba(224,69,69,0.10)"
      : hue === "cyan"
        ? "rgba(107,198,224,0.08)"
        : "rgba(241,203,107,0.07)";
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        background: `radial-gradient(ellipse 65% 90% at 50% -10%, ${c} 0%, transparent 55%),
                     radial-gradient(ellipse 80% 70% at 50% 110%, rgba(0,0,0,0.6) 0%, transparent 60%)`,
        opacity: intensity,
      }}
    />
  );
}

export function Vignette({ strength = 1 }: { strength?: number }) {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        background: `radial-gradient(ellipse 80% 70% at 50% 50%, transparent 30%, rgba(0,0,0,${0.65 * strength}) 100%)`,
      }}
    />
  );
}

export function Dust({
  count = 40,
  intensity = 1,
  color = "gold",
}: {
  count?: number;
  intensity?: number;
  color?: "gold" | "red" | "cyan";
}) {
  const t = useTime();
  const seeds = useMemo(() => {
    let s = 13;
    const r = (): number => {
      s = (s * 9301 + 49297) % 233280;
      return s / 233280;
    };
    return Array.from({ length: count }).map((_, i) => ({
      i,
      x0: r(),
      y0: r(),
      drift: 0.3 + r() * 0.7,
      size: 1 + r() * 2.4,
      speed: 0.05 + r() * 0.12,
      twinkle: r() * Math.PI * 2,
      offset: r(),
    }));
  }, [count]);
  const c =
    color === "red"
      ? "rgba(224,69,69,0.7)"
      : color === "cyan"
        ? "rgba(155,224,242,0.7)"
        : "rgba(255,232,168,0.7)";
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        overflow: "hidden",
        mixBlendMode: "screen",
      }}
    >
      {seeds.map((s) => {
        const yProg = ((s.y0 + t * s.speed) % 1.4) - 0.2;
        const x = s.x0 * 100 + Math.sin(t * s.drift + s.offset * 6) * 4;
        const y = yProg * 100;
        const tw = 0.4 + 0.6 * Math.abs(Math.sin(t * 0.8 + s.twinkle));
        return (
          <div
            key={s.i}
            style={{
              position: "absolute",
              left: `${x}%`,
              top: `${y}%`,
              width: s.size,
              height: s.size,
              borderRadius: "50%",
              background: c,
              opacity: tw * intensity,
              filter: `blur(${s.size > 2 ? 0.6 : 0}px) drop-shadow(0 0 4px ${c})`,
            }}
          />
        );
      })}
    </div>
  );
}

export function Grain({ opacity = 0.05 }: { opacity?: number }) {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 220 220' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3CfeColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.6 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        opacity,
        mixBlendMode: "overlay",
      }}
    />
  );
}
